/**
 * StepList — the numbered step shell every Marketing channel panel uses (listing-marketing-hub BR-12):
 * an ordered list whose items carry a step number, a short title and the step's own content (text,
 * callouts and the copy boxes that step uses). Callout is the quirk box placed inside a step.
 */
import type { ReactNode } from 'react';
import { Info, type LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface MarketingStep {
  title: string;
  children: ReactNode;
}

interface StepListProps {
  steps: MarketingStep[];
  testId: string;
}

export function StepList({ steps, testId }: StepListProps) {
  return (
    <ol className="flex flex-col" data-testid={testId}>
      {steps.map((step, i) => (
        <li
          key={step.title}
          data-testid={`${testId}-${i + 1}`}
          className="flex gap-3 border-t border-border py-5 first:border-t-0 first:pt-0 last:pb-0"
        >
          <span
            aria-hidden="true"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-border bg-muted text-sm font-semibold text-foreground"
          >
            {i + 1}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <h3 className="pt-0.5 text-sm font-semibold text-foreground">{step.title}</h3>
            {step.children}
          </div>
        </li>
      ))}
    </ol>
  );
}

interface CalloutProps {
  title: string;
  children: ReactNode;
  testId: string;
  /** Defaults to the info glyph. */
  icon?: LucideIcon;
}

/** A site quirk the landlord must act on, inside the step it affects. */
export function Callout({ title, children, testId, icon: Icon = Info }: CalloutProps) {
  return (
    <div data-testid={testId} className="flex items-start gap-3 rounded-md border border-info/25 bg-info/10 p-3 text-sm">
      <Icon aria-hidden="true" className="mt-0.5 h-[18px] w-[18px] shrink-0 text-info" />
      <div className="min-w-0 space-y-0.5">
        <p className="font-semibold text-info">{title}</p>
        <div className="text-foreground">{children}</div>
      </div>
    </div>
  );
}

interface PostingControlsProps {
  /** Posting is off (the listing is paused or closed): the controls inside cannot be used. */
  off: boolean;
  children: ReactNode;
}

/**
 * The copy and posting controls of a channel panel. While posting is off they are inert and dimmed
 * (a disabled control is exempt from text contrast) but the guide text around them keeps full
 * contrast, and the Help link and disclosures, which sit outside this wrapper, stay usable (BR-1).
 */
export function PostingControls({ off, children }: PostingControlsProps) {
  return (
    <div
      className={cn('flex min-w-0 flex-col gap-3', off && '[&_a]:opacity-60 [&_button]:opacity-60 [&_input]:opacity-60 [&_textarea]:opacity-60')}
      data-posting-off={off || undefined}
      // `inert` also removes the controls from the tab order and the accessibility tree.
      ref={(el) => el?.toggleAttribute('inert', off)}
    >
      {children}
    </div>
  );
}
